import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  View,
  Text,
  Button,
  Flex,
  Badge,
  Loader,
} from "@aws-amplify/ui-react";
import {
  getOutdatedShares,
  updateSharesWithCurrentData,
  type OutdatedShare,
} from "../utils/shareSync";

export default function OutdatedSharesWidget() {
  const navigate = useNavigate();
  const [outdatedShares, setOutdatedShares] = useState<OutdatedShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    async function checkOutdatedShares() {
      try {
        const outdated = await getOutdatedShares();
        setOutdatedShares(outdated);
      } catch (error) {
        console.warn("Failed to check outdated shares:", error);
      } finally {
        setLoading(false);
      }
    }

    checkOutdatedShares();
  }, []);

  const handleUpdateAll = async () => {
    setUpdating(true);
    try {
      const shareIds = outdatedShares.map((share) => share.id);
      await updateSharesWithCurrentData(shareIds);
      setOutdatedShares([]);
    } catch (error) {
      console.error("Failed to update shares:", error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Card variation="outlined" padding="1rem">
        <Flex alignItems="center" gap="1rem">
          <Loader size="small" />
          <Text>Checking shared skills...</Text>
        </Flex>
      </Card>
    );
  }

  if (outdatedShares.length === 0) {
    return null;
  }

  const totalShares = outdatedShares.length;
  const maxDaysBehind = Math.max(...outdatedShares.map((s) => s.daysBehind));

  return (
    <Card
      variation="outlined"
      marginBottom="2rem"
      style={{
        background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        color: "white",
        border: "none",
      }}
    >
      <Flex alignItems="center" gap="1rem" padding="1.5rem">
        <Text fontSize="2rem">🔄</Text>
        <View flex="1">
          <Text
            fontSize="1.1rem"
            fontWeight="bold"
            color="white"
            marginBottom="0.25rem"
          >
            {totalShares} Shared Skill{totalShares > 1 ? "s" : ""} Out of Date
          </Text>
          <Text fontSize="0.9rem" color="rgba(255, 255, 255, 0.9)">
            {maxDaysBehind} day{maxDaysBehind > 1 ? "s" : ""} behind your latest
            updates
          </Text>
        </View>
        <Flex gap="0.5rem">
          <Button
            size="small"
            variation="link"
            onClick={() => navigate("/app/shared-skills")}
            style={{
              color: "white",
              textDecoration: "underline",
            }}
          >
            View Details
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={handleUpdateAll}
            isLoading={updating}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              color: "white",
            }}
          >
            Update All
          </Button>
        </Flex>
      </Flex>

      {/* Show first few outdated shares */}
      {outdatedShares.slice(0, 3).map((share) => (
        <View
          key={share.id}
          padding="0.5rem 1.5rem"
          style={{
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            fontSize: "0.85rem",
          }}
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Text color="rgba(255, 255, 255, 0.9)">{share.recipientEmail}</Text>
            <Badge
              variation="warning"
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                color: "white",
              }}
            >
              {share.daysBehind}d behind
            </Badge>
          </Flex>
        </View>
      ))}

      {outdatedShares.length > 3 && (
        <View
          padding="0.5rem 1.5rem"
          style={{
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            fontSize: "0.85rem",
          }}
        >
          <Text color="rgba(255, 255, 255, 0.9)">
            +{outdatedShares.length - 3} more outdated shares
          </Text>
        </View>
      )}
    </Card>
  );
}
